<script setup lang="ts">
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import PageTitle from "@/components/settings/PageTitle.vue";
import Translator from "@/utils/common/Translator.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import {computed} from "vue";
import DTOFileAttachmentInfo = namaTypes.DTOFileAttachmentInfo;
import ChangePasswordRequest = namaTypes.ChangePasswordRequest;

const userInfoList = computed(() => [
  {id: 'code', value: LoginUtils.loginInfo?.userInfo?.code},
  {id: 'name1', value: LoginUtils.loginInfo?.userInfo?.name1},
  {id: 'name2', value: LoginUtils.loginInfo?.userInfo?.name2},
]);
const displayName = computed(() =>
  LoginUtils.loginInfo?.userInfo?.name1 || LoginUtils.loginInfo?.userInfo?.name2 || '');
const initials = computed(() => displayName.value.trim().charAt(0) || '?');

async function updateUserImage(object: Record<string, any>, removeImage?: boolean) {
  let attachmentInfo: DTOFileAttachmentInfo = object.mainFile?.attachmentInfo ?? {};
  let changePasswordInfo: ChangePasswordRequest = {
    userIdToChangePasswordFor: LoginUtils.currentUserId(),
    imageName: attachmentInfo?.fileName,
    passwordMustBeChanged: false,
    removeImage: removeImage
  };
  let response = await NamaCommon.namaFetch(NamaURLS.Change_Password, {
    params: {userImageFileId: attachmentInfo?.fileId},
    data: changePasswordInfo
  });
  if (response && response.result && response.result.succeded) {
    useMessagesStore().showSuccessMessage()
    LoginUtils.userImageSrc.value = LoginUtils.getCurrentUserImageSrc();
  }
}

async function openUpdateImgDialog() {
  let saveBtn: CommonDialogAction = new CommonDialogAction('save', async (object: Record<string, any>) => {
    await updateUserImage(object);
  });
  let removeBtn: CommonDialogAction = new CommonDialogAction('removeImage', async () => {
    await updateUserImage({}, true);
  });
  let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
  });
  let presenter: QuestionFieldsPresenter = new QuestionFieldsPresenter();
  await presenter.edit([NamaCommon.createQuestionField("mainFile", FieldType.Binary)], []);
  let dialogBox = new DialogHandler([saveBtn, removeBtn, cancelBtn], Translator.translateId("UpdateUserImage"), true);
  await dialogBox.createPresenter(presenter as WidgetsPresenter);
  await dialogBox.runDialogBox();
}
</script>

<template>
  <div class="tw-mx-auto tw-w-full tw-max-w-6xl tw-space-y-6 tw-px-6">
    <section
      class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-namaBackground-deep-light tw-p-4
             dark:tw-border-gray-700 dark:tw-bg-namaBackground-deep-dark">
      <PageTitle title="myProfile" icon="person"/>
      <div class="tw-mt-6 tw-flex tw-flex-col tw-items-center tw-gap-3">
        <div class="tw-group tw-relative tw-size-28 tw-shrink-0">
          <q-avatar
            size="112px"
            class="tw-border tw-border-gray-200 tw-bg-namaBackground-dim-light tw-text-2xl
                   dark:tw-border-gray-700 dark:tw-bg-namaBackground-dim-dark">
            <img v-if="LoginUtils.userImageSrc.value" :src="LoginUtils.userImageSrc.value" alt="">
            <span v-else class="tw-text-namaGray dark:tw-text-gray-400" v-text="initials"/>
          </q-avatar>

          <button
            type="button"
            :title="Translator.translateId('UpdateUserImage')"
            :aria-label="Translator.translateId('UpdateUserImage')"
            class="tw-absolute tw-bottom-0 tw-start-0 tw-flex tw-size-9 tw-items-center tw-justify-center
                   tw-rounded-full tw-border tw-border-gray-200 tw-bg-namaBackground-bright-light
                   tw-transition-colors hover:tw-bg-namaBackground-dim-light
                   focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-light
                   dark:tw-border-gray-700 dark:tw-bg-namaBackground-bright-dark
                   dark:hover:tw-bg-namaBackground-dim-dark dark:focus-visible:tw-ring-primary-dark"
            @click.stop="openUpdateImgDialog">
            <NamaIcon default-icon="nm-add-image" class="!tw-size-4 active-primary"/>
          </button>
        </div>

        <p class="tw-text-base tw-font-medium tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
           v-text="displayName"/>
      </div>

      <dl class="tw-max-w-[480px] tw-w-full tw-min-w-fit tw-justify-self-center tw-mt-6 tw-divide-y tw-divide-gray-200 tw-border-t tw-border-gray-200
                 dark:tw-divide-gray-700 dark:tw-border-gray-700">
        <div v-for="info in userInfoList" :key="info.id"
             class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-3">
          <dt class="tw-text-sm tw-text-namaGray dark:tw-text-gray-400"
              v-text="Translator.translateId(info.id)"/>
          <dd class="tw-text-sm tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
              v-text="info.value"/>
        </div>
      </dl>
    </section>
  </div>
</template>

<style scoped>

</style>
